import type { MeetingSegmentIdentity } from '@electron/shared/meetings.types';

const PREVIOUS_RECORDING_WINDOW_MS = 120_000;

type DetectedMeetingApp = {
  name?: string;
  bundleIdentifier?: string;
  meetingIdentity?: MeetingSegmentIdentity;
};

type StoppedRecording = {
  meetingId: string;
  stoppedAtMs: number;
  stopReason: string;
  meetingIdentityKey: string | undefined;
};

let latestDetectedApps: DetectedMeetingApp[] = [];
let latestDetectionAtMs = 0;
let recordingMeetingIdentity: { meetingId: string; key: string } | undefined;
let previousRecording: StoppedRecording | undefined;

export function normalizeAppBundleId(bundleIdentifier?: string) {
  return bundleIdentifier?.trim().toLowerCase();
}

export function getSessionAppId(app: {
  name?: string;
  bundleIdentifier?: string;
}) {
  return app.bundleIdentifier ?? app.name;
}

function findMeetingIdentityKey(appBundleId: string | undefined) {
  const bundleId = normalizeAppBundleId(appBundleId);
  if (!bundleId) return undefined;
  const identity = latestDetectedApps.find(
    (app) => normalizeAppBundleId(getSessionAppId(app)) === bundleId
  )?.meetingIdentity;
  return identity && `${identity.providerName}:${identity.meetingKey}`;
}

export function noteDetectedMeetingApps(
  apps: DetectedMeetingApp[],
  recording: { meetingId: string; appBundleId: string | undefined } | undefined
) {
  latestDetectedApps = apps;
  latestDetectionAtMs = Date.now();
  if (!recording) return;
  const keyWhileAppStillDetected = findMeetingIdentityKey(
    recording.appBundleId
  );
  if (keyWhileAppStillDetected) {
    recordingMeetingIdentity = {
      meetingId: recording.meetingId,
      key: keyWhileAppStillDetected,
    };
  }
}

export function noteRecordingStopped(
  meetingId: string,
  stopReason: string,
  stoppedAtMs: number
) {
  previousRecording = {
    meetingId,
    stoppedAtMs,
    stopReason,
    meetingIdentityKey:
      recordingMeetingIdentity?.meetingId === meetingId
        ? recordingMeetingIdentity.key
        : undefined,
  };
}

export function noteRecordingStarted(
  meetingId: string,
  appBundleId: string | undefined
) {
  const startedAtMs = Date.now();
  const meetingIdentityKey = findMeetingIdentityKey(appBundleId);
  recordingMeetingIdentity = meetingIdentityKey
    ? { meetingId, key: meetingIdentityKey }
    : undefined;

  const previous = previousRecording;
  if (!previous) return {};
  const msSincePreviousStop = startedAtMs - previous.stoppedAtMs;
  if (msSincePreviousStop > PREVIOUS_RECORDING_WINDOW_MS) return {};

  const appListChangedSinceStop = latestDetectionAtMs > previous.stoppedAtMs;
  const meetingIdentitiesComparable =
    previous.meetingIdentityKey !== undefined &&
    meetingIdentityKey !== undefined &&
    appListChangedSinceStop;
  return {
    previous_recording_id: previous.meetingId,
    seconds_since_previous_stop: Math.round(msSincePreviousStop / 1000),
    previous_stop_reason: previous.stopReason,
    ...(meetingIdentitiesComparable && {
      same_meeting_as_previous:
        previous.meetingIdentityKey === meetingIdentityKey,
    }),
  };
}
